import {
  ArrowUpRight,
  Filter,
  MoreHorizontal,
  Plus,
  Search,
} from "lucide-react";
import { useDeferredValue, useMemo, useState } from "react";
import type { QodeItem, QodeStatus, TimelineEvent } from "../types";
import { QrPreview } from "../components/QrPreview";
import { absoluteShortUrl, formatRelativeTime } from "../lib/format";
import { QodeDetailDrawer } from "./QodeDetailDrawer";

type QodesViewProps = {
  capabilities: {
    operate: boolean;
    approve: boolean;
  };
  qodes: QodeItem[];
  onCreateQode: () => void;
  onQodeChanged: (qode: QodeItem, event?: TimelineEvent | null) => void;
};

const statusLabels: Record<QodeStatus, string> = {
  active: "正常",
  degraded: "需处理",
  draft: "草稿",
  paused: "已暂停",
};

export function QodesView({
  capabilities,
  qodes,
  onCreateQode,
  onQodeChanged,
}: QodesViewProps) {
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState<QodeStatus | "all">("all");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const deferredQuery = useDeferredValue(query.trim().toLowerCase());
  const selectedQode = qodes.find((qode) => qode.id === selectedId) ?? null;
  const filteredQodes = useMemo(
    () =>
      qodes.filter((qode) => {
        const matchesStatus = status === "all" || qode.status === status;
        const haystack =
          `${qode.name} ${qode.shortUrl} ${qode.destination} ${qode.purpose}`.toLowerCase();
        return matchesStatus && (!deferredQuery || haystack.includes(deferredQuery));
      }),
    [deferredQuery, qodes, status],
  );

  return (
    <div className="page">
      <section className="page-heading">
        <div>
          <h1>Qodes</h1>
          <p>管理每一个数字入口的状态、现实部署和运行责任。</p>
        </div>
        <button
          className="primary-button"
          type="button"
          disabled={!capabilities.operate}
          onClick={onCreateQode}
        >
          <Plus size={16} />
          新建 Qode
        </button>
      </section>

      <section className="table-toolbar">
        <label>
          <Search size={16} />
          <input
            value={query}
            placeholder="搜索名称、短链接或目标"
            onChange={(event) => setQuery(event.target.value)}
          />
        </label>
        <label className="filter-control">
          <Filter size={15} />
          <select
            aria-label="按状态筛选"
            value={status}
            onChange={(event) =>
              setStatus(event.target.value as QodeStatus | "all")
            }
          >
            <option value="all">全部状态</option>
            <option value="active">正常</option>
            <option value="degraded">需处理</option>
            <option value="draft">草稿</option>
            <option value="paused">已暂停</option>
          </select>
        </label>
      </section>

      <section className="qode-table">
        <div className="qode-table-head">
          <span>入口</span>
          <span>状态</span>
          <span>现实部署</span>
          <span>扫描</span>
          <span>最近变化</span>
          <span />
        </div>
        {filteredQodes.map((qode) => (
          <article className="qode-table-row" key={qode.id}>
            <div className="qode-identity">
              <QrPreview value={absoluteShortUrl(qode.shortUrl)} size={58} />
              <div>
                <strong>{qode.name}</strong>
                <span>{qode.shortUrl}</span>
                <small>{qode.purpose}</small>
              </div>
            </div>
            <div>
              <span className={`status status-${qode.status}`}>
                <i />
                {statusLabels[qode.status]}
              </span>
            </div>
            <div className="numeric-cell">
              <strong>{qode.deployments}</strong>
              <span>个载体</span>
            </div>
            <div className="numeric-cell">
              <strong>{qode.scans.toLocaleString()}</strong>
              <span>累计</span>
            </div>
            <div className="updated-cell">
              <span>{formatRelativeTime(qode.updatedAt)}</span>
              <small>{qode.destination}</small>
            </div>
            <div className="row-buttons">
              <a
                className={
                  qode.status === "active" || qode.status === "degraded"
                    ? ""
                    : "disabled-link"
                }
                href={
                  qode.status === "active" || qode.status === "degraded"
                    ? absoluteShortUrl(qode.shortUrl)
                    : undefined
                }
                target="_blank"
                rel="noreferrer"
                aria-label={`测试 ${qode.name}`}
                aria-disabled={
                  qode.status !== "active" && qode.status !== "degraded"
                }
              >
                <ArrowUpRight size={17} />
              </a>
              <button
                type="button"
                aria-label={`治理 ${qode.name}`}
                onClick={() => setSelectedId(qode.id)}
              >
                <MoreHorizontal size={17} />
              </button>
            </div>
          </article>
        ))}
        {!filteredQodes.length ? (
          <div className="qode-empty">没有符合当前条件的 Qode。</div>
        ) : null}
      </section>
      {selectedQode ? (
        <QodeDetailDrawer
          capabilities={capabilities}
          qode={selectedQode}
          onClose={() => setSelectedId(null)}
          onQodeChanged={onQodeChanged}
        />
      ) : null}
    </div>
  );
}
